import React, { useEffect, useState, useMemo } from 'react';
import { ArrowDropDownIcon, ArrowDropUpIcon, Autocomplete, Button, ButtonGroup, CheckIcon, ClearIcon, DialogActions, DialogContent, Fab, Grid, IconButton, InfoIcon, InputAdornment, Stack, TextField, Typography } from "../../styles/MaterialUI";
import ContentPlaylist from '../ContentPlaylist/ContentPlaylist';
import { useDebounce } from '../../hooks/useDebounce';
import axios from 'axios';
import apiConstants, { INSIGHTS_URL } from '../../constants/apiConstants';
import { useDispatch, useSelector } from 'react-redux';
import CustomTooltip from '../StyledComponents/CustomTooltip';
import { resetDashboardState } from '../Redux/Features/addContentSlice';
import { Tooltip } from '@mui/material';
import { buildContentTypeOptions } from '../../utils/contentTypeUtils';
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";


const CreateContentDialog = ({
    openDashboardMenu,
    showAlert,
    createContent,
    closeAddContentModal,
    // Pass the full userDetails object (from the /userDetails API) so we can
    // read org_properties.aristocast_limits and permitted_content_types.
    userDetails,
    // Pass the current total content count for the org so we can enforce max_contents.
    currentContentCount = 0,
    // Pass the current total storage used (in GB) so we can enforce max_content_storage_limit.
    currentStorageUsedGB = 0,
}) => {
    const { organizationId } = useSelector((state) => state.userLogin);
    const orgConfig = useOrgConfigData("createContent");
    const {
        org_properties: org_configurations,
        all_content_types,
        permitted_content_types
    } = orgConfig || {};
    const { content_Module, content_Workbook, view_WorkbookName, dashboard_List, dashboard_URL_List, breadcrumb_URL } = useSelector((state) => state.addContent);
    const { userToken } = useSelector((state) => state.userToken);
    const headers = { Authorization: `Bearer ${userToken}` };



    // ── Limits derived from org_properties ──────────────────────────────────
    // ── Video size guard ─────────────────────────────────────────────────────
    /**
     * Returns true if the video file is within the allowed size limit.
     * Uses the same tiered message logic described in the spec.
     *   < configured limit  → "Upgrade to upload larger Videos"
     *   = 10 MB (default)   → "Cannot upload videos larger than 10 MB"
     */

    const DEFAULT_PLATFORM_VIDEO_LIMIT_MB = 150; // reference value from spec
    const aristocastLimits = org_configurations?.aristocast_limits ?? {};
    const maxContents = aristocastLimits.max_limit_of_aristocast_contents ?? 0;
    const maxContentPerPlaylist = aristocastLimits.max_content_per_playlist ?? 0;
    const videoSizeLimitMB = aristocastLimits.video_content_size_limit?.value ?? 0;
    const maxStorageLimitGB = aristocastLimits.max_content_storage_limit?.value ?? 0;
    const videoSizeLimitUnit = aristocastLimits.video_content_size_limit?.unit ?? 0;
    const maxStorageLimitUnit = aristocastLimits.max_content_storage_limit?.unit ?? 0;

    // ── Content-type dropdown options ────────────────────────────────────────
    const allContentTypes = all_content_types ?? [];
    const permittedContentTypes = permitted_content_types ?? [];


    // ── Form state ───────────────────────────────────────────────────────────
    const [addContentName, setAddContentName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [addContentStatus, setAddContentStatus] = useState(null);
    const [addContentType, setAddContentType] = useState(null);
    const [addContentChooseFit, setAddContentChooseFit] = useState("Original");
    const [fileSrc, setFileSrc] = useState(null);
    const [thumbnailName, setThumbnailName] = useState(null);
    const [videoFileName, setVideoFileName] = useState(null);
    const errorMessages = {
        contentNameError: "", contentStatusError: "", contentTypeError: "",
        workbookError: "", dashboardError: "", customViewError: "",
        dashboardTypeError: "", metricsDashboardUrlError: "", openUrlError: ""
    };
    const [addContentErrorMessages, setAddContentErrorMessages] = useState(errorMessages);
    const debounceValue = useDebounce(addContentName);
    const isWideContent = (addContentType === "IntelliDash Dashboard" || addContentType === "Content Playlist" || addContentType === "Machine Metrics");
    const [contentPlaylistData, setContentPlayListData] = useState([]);
    const dispatch = useDispatch();

    const [contentModule, setContentModule] = useState("");
    const [contentWorkbook, setContentWorkbook] = useState("");
    const [viewWorkbookName, setViewWorkbookName] = useState("");
    const [contentDashboard, setContentDashboard] = useState("");
    const [contentView, setContentView] = useState("Original");
    const [dashboardList, setDashboardList] = useState([]);
    const [dashboardUrlList, setDashboardUrlList] = useState([]);
    const [customViewList, setCustomViewList] = useState([]);
    const [viewIdData, setViewIdData] = useState([]);
    const [breadcrumUrl, setBreadcrumUrl] = useState("");
    const [enablePreview, setEnablePreview] = useState(true);

    const [dashboardType, setDashboardType] = useState(null);
    const [metricsDashboardUrl, setMetricsDashboardUrl] = useState("");
    const [metricsDashboardTypeList, setMetricsDashboardTypeList] = useState([]);
    const [orgIntegrationData, setOrgIntegrationData] = useState([]);
    const [integrationActiveFlag, setIntegrationActiveFlag] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [addContentOpenUrl, setAddContentOpenUrl] = useState("");

    const refreshRateOptions = [
        { label: "None", value: "None" }, { label: "5 Mins", value: "5" }, { label: "10 Mins", value: "10" },
        { label: "15 Mins", value: "15" }, { label: "20 Mins", value: "20" }, { label: "25 Mins", value: "25" },
        { label: "30 Mins", value: "30" }, { label: "35 Mins", value: "35" }, { label: "40 Mins", value: "40" },
        { label: "45 Mins", value: "45" }, { label: "50 Mins", value: "50" }, { label: "55 Mins", value: "55" },
        { label: "60 Mins", value: "60" },
    ];
    const [addContentRefreshRate, setAddContentRefreshRate] = useState(refreshRateOptions[0]);

    // Converts a limit value from `unit` into MB.
    const toMB = (value, unit = "MB") => {
        const u = String(unit).toUpperCase();
        if (u === "KB") return value / 1024;
        if (u === "MB") return value;
        if (u === "GB") return value * 1024;
        if (u === "TB") return value * 1024 * 1024;
        return value; // unknown unit — treat as MB
    };

    // Converts a limit value from `unit` into GB.
    const toGB = (value, unit = "GB") => {
        const u = String(unit).toUpperCase();
        if (u === "KB") return value / (1024 * 1024);
        if (u === "MB") return value / 1024;
        if (u === "GB") return value;
        if (u === "TB") return value * 1024;
        return value; // unknown unit — treat as GB
    };

    useEffect(() => {
        setContentModule(content_Module);
        setContentWorkbook(content_Workbook);
        setViewWorkbookName(view_WorkbookName);
        setDashboardList(dashboard_List);
        setDashboardUrlList(dashboard_URL_List);
        setBreadcrumUrl(breadcrumb_URL);
    }, [content_Module, content_Workbook]);

    useEffect(() => {
        const check_contentname = async () => {
            const reqPayload = { contentName: debounceValue, orgId: organizationId };
            try {
                if (debounceValue?.trim().length) {
                    const response = await axios.post(`${apiConstants.GET_API_URL}checkContentName`, reqPayload, { headers });
                    const isAvailable = response.data.available;
                    !isAvailable
                        ? setErrorMessage("Content Name already in use. Try a different Content Name")
                        : setErrorMessage("");
                }
            } catch (error) {
                const { statusCode, message, redirect_url } = error.response.data;
                showAlert("error", message);
                if (statusCode === 403) window.open(redirect_url, "_self");
            }
        };
        check_contentname();
    }, [debounceValue]);

    // ── Max-contents guard ───────────────────────────────────────────────────
    /**
     * Call this before opening the "Add Content" flow.
     * Returns true if the user can proceed, false if they've hit their limit.
     */
    const checkMaxContentsLimit = () => {

        if (currentContentCount >= maxContents) {
            showAlert(
                "error",
                "You have reached your maximum content limit. Delete existing contents to add a new one or upgrade to add more contents."
            );
            return false;
        }
        return true;
    };

    // ── Max-storage guard ────────────────────────────────────────────────────
    /**
     * Call this before saving content that involves a file upload.
     * fileSizeBytes — the size of the file the user is about to upload.
     * Returns true if the upload would not exceed the storage limit.
     */
    const checkStorageLimit = (fileSizeBytes = 0) => {
        return true;
        const fileSizeGB = fileSizeBytes / (1024 * 1024 * 1024);
        const maxGB = toGB(maxStorageLimitGB, maxStorageLimitUnit);
        if (currentStorageUsedGB + fileSizeGB > maxGB) {
            showAlert(
                "error",
                "Maximum Storage Limit reached. Delete existing Contents to free up space or upgrade to expand storage space."
            );
            return false;
        }
        return true;
    };



    const validateVideoSizeLimit = (fileSizeInMB) => {
        // Convert the configured limit to MB regardless of what unit it was stored in.
        const configuredLimitMB = videoSizeLimitMB
            ? toMB(videoSizeLimitMB, videoSizeLimitUnit)
            : DEFAULT_PLATFORM_VIDEO_LIMIT_MB;
        const effectiveLimit = DEFAULT_PLATFORM_VIDEO_LIMIT_MB;

        if (fileSizeInMB > configuredLimitMB) {
            if (configuredLimitMB && configuredLimitMB < DEFAULT_PLATFORM_VIDEO_LIMIT_MB) {
                showAlert(
                    "error",
                    "The uploaded video exceeds the maximum allowed video size limit. Upgrade to upload larger Videos."
                );
            } else {
                showAlert("error", `Cannot upload videos larger than ${effectiveLimit} MB`);
            }
            return false;
        }
        return true;
    };

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleContentNameChange = (e) => {
        setAddContentName(e.target.value);
    };

    const handleOpenUrlChange = (e) => {
        const { value } = e.target;
        if (value.trim()) setAddContentErrorMessages({ openUrlError: "" });
        setAddContentOpenUrl(value);
    };

    const validateImageFormat = (event) => {
        const fileInput = event.target;
        if (fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);
                if (fileSizeInMB > 50) {
                    showAlert("error", "File size exceeds 50MB. Please upload a smaller image");
                    fileInput.value = "";
                    setThumbnailName(null);
                    return;
                }
                // Storage limit check at file-selection time for images
                if (!checkStorageLimit(selectedFile.size)) {
                    fileInput.value = "";
                    setThumbnailName(null);
                    return;
                }
                setFileSrc(selectedFile);
                setThumbnailName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select an image file");
                fileInput.value = "";
                setThumbnailName(null);
            }
        } else {
            setThumbnailName(null);
        }
    };

    const validateVideoFormat = (event) => {
        const fileInput = event.target;
        if (fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["video/mp4", "video/quicktime", "video/x-ms-wmv", "video/avi", "video/x-msvideo"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);

                // ── NEW: per-org video size limit check ──────────────────
                if (!validateVideoSizeLimit(fileSizeInMB)) {
                    fileInput.value = "";
                    setVideoFileName(null);
                    return;
                }

                // ── NEW: storage limit check ─────────────────────────────
                if (!checkStorageLimit(selectedFile.size)) {
                    fileInput.value = "";
                    setVideoFileName(null);
                    return;
                }

                setFileSrc(selectedFile);
                setVideoFileName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select a video file");
                fileInput.value = "";
                setVideoFileName(null);
            }
        } else {
            setVideoFileName(null);
        }
    };

    const handleAddContentPlaylist = (contentPlaylist) => {
        const settingsMap = { "Play Once": 1, "Play Twice": 2, "Play Thrice": 3 };
        const updatedContent = contentPlaylist.map((content, index) => ({
            ...content,
            cSettingsType: content.cType === "video" ? "loop" : "mins",
            cOrder: index + 1,
            cSettingsValue: content.cType === "video"
                ? settingsMap[content.cSettings] || content.cSettings
                : content.cSettings,
        }));
        setContentPlayListData(updatedContent);
    };

    const contentPlaylistError = (errorMessage) => {
        showAlert("error", errorMessage);
    };

    const handleDashboardChange = async (newValue) => {
        setEnablePreview(false);
        try {
            const newBreadcrumUrl = newValue
                ? `${contentModule}->${contentWorkbook}->${newValue}: ${contentView ? `View-${contentView}` : ""}`
                : `${contentModule}->${contentWorkbook}`;

            setBreadcrumUrl(newBreadcrumUrl);
            setAddContentErrorMessages({ dashboardError: newValue ? "" : addContentErrorMessages.dashboardError });
            setContentDashboard(newValue);

            if (newValue) {
                const response = await axios.post(
                    `${INSIGHTS_URL.CAST_API_URL}usercustomviewList`,
                    { view_workbook: viewWorkbookName, dashboard_name: newValue },
                    { headers }
                );
                const { data } = response.data;
                const responseData = data.map((view) => view.cust_view_name);
                responseData.unshift("Original");
                setCustomViewList(responseData);
                setViewIdData(data);
            } else {
                setCustomViewList(["Original"]);
                setViewIdData([]);
            }
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) window.open(redirect_url, "_self");
        }
    };

    const getMetricsDashboardTypes = async () => {
        try {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getDashboardTypes`, {}, { headers });
            const { data } = apiResponse.data;
            setMetricsDashboardTypeList(data);
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) window.open(redirect_url, "_self");
        }
    };

    const getIntegrationsData = async () => {
        try {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
            const { data } = apiResponse.data;
            const activeFlag = data?.[0]?.ic_activeflag;
            setOrgIntegrationData(data);
            setIntegrationActiveFlag(activeFlag);
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) window.open(redirect_url, "_self");
        }
    };

    useEffect(() => {
        getIntegrationsData();
    }, []);

    const handleAddContentTypeChange = (newValue) => {
        // newValue here is the option object { label, disabled } or null
        const label = newValue?.label ?? null;
        setAddContentType(label);
        setAddContentChooseFit("Original");
        setFileSrc(null);
        setThumbnailName(null);
        setVideoFileName(null);
        if (label) setAddContentErrorMessages({ contentTypeError: "" });
        if (label === "Machine Metrics") {
            getMetricsDashboardTypes();
            getIntegrationsData();
        }
        setContentModule("");
        setContentWorkbook("");
        setContentDashboard("");
        setViewWorkbookName("");
        setDashboardList([]);
        setDashboardUrlList([]);
        setContentView("Original");
        setBreadcrumUrl("");
    };

    const handleSelectWorkbook = () => {
        setEnablePreview(false);
        setAddContentErrorMessages({ workbookError: "" });
        setContentModule("");
        setContentWorkbook("");
        setContentDashboard("");
        setViewWorkbookName("");
        setDashboardList([]);
        setDashboardUrlList([]);
        setViewIdData([]);
        setContentView("Original");
        setBreadcrumUrl("");
        openDashboardMenu();
        dispatch(resetDashboardState());
    };

    const generateDashboardUrl = (dashboard_UrlList, viewType, viewList = []) => {
        const filteredData = dashboard_UrlList.find((data) => data.view_name === contentDashboard);
        if (!filteredData) return "";
        const [, workbookName, dashboardName] = filteredData.view_portal_url.split("~");
        const baseURL = `https://intellitab.intellidash.net/#/site/intelli/views/${workbookName}/${dashboardName}`;
        if (viewType === "Original") return baseURL;
        const view = viewList.find((view) => view.cust_view_name === viewType);
        return view ? `${baseURL}/${view.cv_luid}/${view.cv_url_id}` : false;
    };

    const validateContentPlaylist = (playlistData) => {
        if (!playlistData.length) {
            return { isError: true, errorMessage: "Please include at least one content in the playlist." };
        }
        const content = new Set();
        for (const item of playlistData) {
            const { cType, cName, cSettings, cStatus } = item;
            if (!cType || !cName || !cSettings || !cStatus) {
                return { isError: true, errorMessage: "All fields in the playlist must be filled in." };
            }
            const uniqueKey = `${cType}-${cName}`;
            if (content.has(uniqueKey)) {
                return { isError: true, errorMessage: `An entry with content type: ${cType} and content name: ${cName} already exists.` };
            }
            content.add(uniqueKey);
        }
        return { isError: false, errorMessage: "" };
    };

    const getIntegrationByName = (name) =>
        orgIntegrationData.find((item) => item.integration_name === name);

    const isIntegrationActive = (name) =>
        orgIntegrationData.some((item) => item.integration_name === name && item.ic_activeflag === 1);

    const handleSaveContent = async (e) => {
        e.preventDefault();

        // ── NEW: max-contents check before anything else ─────────────────────
        if (!checkMaxContentsLimit()) return;

        const error = {};
        if (!addContentName.trim()) {
            setErrorMessage("Content name cannot be empty");
            return;
        }
        if (!addContentStatus) {
            error.contentStatusError = "Please select content status";
        } else if (!addContentType) {
            error.contentTypeError = "Please select content type";
        }
        if (addContentType === "IntelliDash Dashboard") {
            if (!contentWorkbook) error.workbookError = "Workbook cannot be empty";
            else if (!contentDashboard) error.dashboardError = "Please select dashboard";
            else if (!contentView) error.customViewError = "Please select view";
        }
        if (addContentType === "Machine Metrics") {
            if (!dashboardType) error.dashboardTypeError = "Please select dashboard type";
            else if (!metricsDashboardUrl) error.metricsDashboardUrlError = "Please enter dashboard url";
        }
        if (addContentType === "Acumatica") {
            if (!metricsDashboardUrl) error.metricsDashboardUrlError = "Please enter dashboard url";
        }
        if (addContentType === "Open URL") {
            if (!addContentOpenUrl.trim()) error.openUrlError = "URL cannot be empty";
        }
        if (Object.keys(error).length > 0) {
            setAddContentErrorMessages(error);
            return;
        }

        // ── NEW: storage limit re-check at save time (covers edge cases) ─────
        if ((addContentType === "Image" || addContentType === "Video") && fileSrc) {
            if (!checkStorageLimit(fileSrc.size)) return;
        }

        if (addContentType === "Image" && !fileSrc) {
            showAlert("error", "Please upload an image");
            return;
        }
        if (addContentType === "Video" && !fileSrc) {
            showAlert("error", "Please upload a video");
            return;
        }

        const contentTypeMap = {
            "IntelliDash Dashboard": "dashboard",
            "Content Playlist": "content_playlist",
            "Machine Metrics": "orchestrated_url",
            "Open URL": "open_url",
            "Acumatica": "orchestrated_url",
        };
        const status = addContentStatus === "Enabled" ? 1 : 0;
        const contentType = contentTypeMap[addContentType] || addContentType.toLowerCase();

        const formData = new FormData();
        formData.append("org_id", organizationId);
        formData.append("content_name", addContentName.trim());
        formData.append("content_type", contentType);
        formData.append("content_status", status);

        if (addContentType === "IntelliDash Dashboard") {
            const tableauUrl = generateDashboardUrl(dashboardUrlList, contentView, viewIdData);
            formData.append("dashboard_url", tableauUrl);
            formData.append("dashboard_view", breadcrumUrl);
            formData.append("view_type", contentView);
        }
        if (addContentType === "Machine Metrics") {
            const integration = getIntegrationByName("Machine Metrics");
            formData.append("dashboard_url", metricsDashboardUrl.trim());
            formData.append("org_integration_id", integration?.ic_org_integration_id);
            formData.append("mm_dashboard_id", dashboardType);
            formData.append("dashboard_view", "Original");
        }
        if (addContentType === "Acumatica") {
            const integration = getIntegrationByName("Acumatica");
            formData.append("dashboard_url", metricsDashboardUrl.trim());
            formData.append("refresh_rate", addContentRefreshRate.value);
            formData.append("org_integration_id", integration?.ic_org_integration_id);
            formData.append("dashboard_view", "Original");
        }
        if (addContentType === "Open URL") {
            formData.append("browser_url", addContentOpenUrl.trim());
            formData.append("refresh_rate", addContentRefreshRate.value);
        }
        if (addContentType === "Image" || addContentType === "Video") {
            formData.append("file", fileSrc);
            if (addContentType === "Image") formData.append("fit_type", addContentChooseFit);
        }
        if (addContentType === "Content Playlist") {
            const { isError, errorMessage } = validateContentPlaylist(contentPlaylistData);
            if (isError) { showAlert("error", errorMessage); return; }
            const contentData = contentPlaylistData.map((content) => ({
                content_id: content.content_Id,
                display_setting: content.cSettingsValue,
                display_setting_type: content.cSettingsType,
                content_sequence: content.cOrder,
            }));
            formData.append("playlist_contents", JSON.stringify(contentData));
        }

        const uploadImageVideoFile = { uploadFilename: thumbnailName || videoFileName };
        createContent(formData, addContentType, uploadImageVideoFile);
    };

    const handleDashboardTypeChange = async (newValue) => {
        const { ic_mm_dashboard_id } = newValue || {};
        setDashboardType(ic_mm_dashboard_id);
        if (newValue) setAddContentErrorMessages({ dashboardTypeError: "" });
        setMetricsDashboardUrl("");
    };

    const handleDashboardURLChange = (event) => {
        const { value } = event.target;
        setMetricsDashboardUrl(value);
        if (value.trim()) setAddContentErrorMessages({ metricsDashboardUrlError: "" });
    };

    const handleTooltipOpen = (event) => setAnchorEl(event.currentTarget);
    const handleTooltipClose = () => setAnchorEl(null);

    const open = Boolean(anchorEl);

    const tooltipContent = `
    <span style="font-size: 11px; line-height: 0;">This field should hold the MachineMetrics URL of the dashboard for which you want the AristoCAST content to be created.</span><br/>
    <u><strong style="font-size: 11px;">Steps:</strong></u><br/>
    <span style="font-size: 11px;">1) Login to your MachineMetrics account and navigate to the dashboard of interest.</span><br/>
    <span style="font-size: 11px;">2) Select the necessary Group, View and other parameters on the dashboard.</span><br/>
    <span style="font-size: 11px;">3) Once the dashboard is refreshed, copy the MachineMetrics URL and paste it in this box.</span>
  `;

    const tooltipContentAcumatica = `<span style="font-size: 11px;">This field should hold the URL of the "Acumatica" form that you wish to display on the AristoCAST screen. Ex: https://acumatica-demo.intellidash.net/AcumaticaERP/Main?ScreenId=AM0034DB</span><br/>`;

    const contentTypeOptions = useMemo(() => {
        const options = buildContentTypeOptions(
            allContentTypes,
            permittedContentTypes,
            orgIntegrationData
        );

        // const getId = (id) =>
        //     id ? Number(id.replace(/\D/g, "")) : 9999;

        const sorted = [...options].sort((a, b) => {
            // // 1. enabled first
            // if (a.disabled !== b.disabled) {
            //     return a.disabled ? 1 : -1;
            // }

            // 2. optional: sort by ID inside each group
            return a.ic_content_type_name - b.ic_content_type_name;
        });

        return sorted;
    }, [allContentTypes, permittedContentTypes, orgIntegrationData]);
    // ── Content-type Autocomplete helpers ────────────────────────────────────
    // Find the currently selected option object (needed because options are objects now)
    const selectedTypeOption = contentTypeOptions.find((o) => o.label === addContentType) ?? null;



    return (
        <>
            <form method="post" encType="multipart/form-data">
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {/* Content Name */}
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>
                            <TextField
                                variant="outlined" label="Content Name" size="small" required
                                value={addContentName} autoComplete="off" sx={{ width: "100%" }}
                                onChange={handleContentNameChange}
                                error={!!errorMessage} helperText={errorMessage}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {!errorMessage && addContentName.trim().length > 0 ? <CheckIcon color="success" sx={{ fontSize: "1.2rem" }} /> : ""}
                                            {errorMessage ? <ClearIcon color="error" sx={{ fontSize: "1rem" }} /> : ""}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Content Status */}
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>
                            <Autocomplete
                                size="small" options={["Enabled", "Disabled"]} value={addContentStatus}
                                onChange={(e, value) => {
                                    setAddContentStatus(value);
                                    if (value) setAddContentErrorMessages({ contentStatusError: "" });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Content Status" required
                                        error={!!addContentErrorMessages.contentStatusError}
                                        helperText={addContentErrorMessages.contentStatusError}
                                    />
                                )}
                            />
                        </Grid>

                        {/* ── Content Type – permitted first, restricted greyed out ── */}
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>


                            <Autocomplete
                                size="small"
                                value={selectedTypeOption}
                                options={contentTypeOptions}
                                getOptionLabel={(option) => option?.label ?? ""}
                                getOptionDisabled={(option) => option.disabled}
                                isOptionEqualToValue={(option, value) =>
                                    typeof value === "string"
                                        ? option.label === value
                                        : option.label === value?.label
                                }
                                onChange={(e, newValue) => handleAddContentTypeChange(newValue)}
                                renderOption={(props, option, { index }) => {
                                    const isIntegrationType = ["Machine Metrics", "Acumatica", "Power BI"].includes(option.label);
                                    const integrationInactive = isIntegrationType && !isIntegrationActive(option.label);

                                    const tooltipText = option.disabled
                                        ? integrationInactive
                                            ? "Integration not configured or inactive"
                                            : "Upgrade to create this content type"
                                        : "";

                                    // Show a divider right before the first disabled option in the list
                                    const isFirstDisabled =
                                        option.disabled &&
                                        (index === 0 || !contentTypeOptions[index - 1]?.disabled);

                                    return (
                                        <React.Fragment key={option.label}>
                                            {isFirstDisabled && (
                                                <div style={{ borderTop: "0.5px solid #afa1a1", margin: "2px 0" }} />
                                            )}
                                            <ToolTip
                                                title={tooltipText}
                                                disableHoverListener={!option.disabled}
                                                disableTouchListener={!option.disabled}
                                            >
                                                <span style={{ display: "block", width: "100%" }}>
                                                    <li
                                                        {...props}
                                                        aria-disabled={option.disabled}
                                                        style={{
                                                            ...props.style,
                                                            color: option.disabled ? "#574d4d" : "inherit",
                                                            cursor: option.disabled ? "not-allowed" : "pointer",
                                                            pointerEvents: option.disabled ? "none" : "auto",
                                                            opacity: option.disabled ? 0.6 : 1,
                                                        }}
                                                    >
                                                        {option.label}
                                                    </li>
                                                </span>
                                            </ToolTip>
                                        </React.Fragment>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Content Type"
                                        required
                                        error={!!addContentErrorMessages.contentTypeError}
                                        helperText={addContentErrorMessages.contentTypeError}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Image upload */}
                        {addContentType === "Image" && (
                            <Grid container item xs={12} display="flex" justifyContent="space-between">
                                <Stack>
                                    <Typography>Upload an image</Typography>
                                    <input className="upload-thumbnail" type="file" name="file" accept="image/*" onChange={validateImageFormat} hidden />
                                    <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                        <Fab size="small" variant="extended"
                                            onClick={() => document.querySelector(".upload-thumbnail").click()}
                                            sx={{ width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none", "&:hover": { backgroundColor: "#003366" } }}
                                        >Choose File</Fab>
                                        <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {thumbnailName ?? "No file chosen"}
                                        </span>
                                    </Stack>
                                    <p style={{ fontSize: "0.8rem", marginTop: 0 }}>Image size should not exceed 50 MB.</p>
                                </Stack>
                                <Stack>
                                    <Typography>Choose a fit</Typography>
                                    <ButtonGroup variant="contained" aria-label="buttonGroup">
                                        {["Fill", "Fit", "Stretch", "Original"].map((fit) => (
                                            <Button key={fit} size="small" onClick={() => setAddContentChooseFit(fit)}
                                                style={{ textTransform: "none", backgroundColor: addContentChooseFit === fit ? "#ff8000" : "#003366" }}
                                            >{fit}</Button>
                                        ))}
                                    </ButtonGroup>
                                </Stack>
                            </Grid>
                        )}

                        {/* Content Playlist */}
                        {addContentType === "Content Playlist" && (
                            <ContentPlaylist
                                addPlaylist={handleAddContentPlaylist}
                                showError={contentPlaylistError}
                                // ── NEW: pass the per-org playlist limit so ContentPlaylist
                                //    can enforce it internally when "Add Content to Playlist"
                                //    is clicked. Pass 0 to mean "use legacy 20-item cap".
                                maxContentPerPlaylist={maxContentPerPlaylist}
                            />
                        )}

                        {/* Video upload */}
                        {addContentType === "Video" && (
                            <Grid container item xs={12} display="flex" justifyContent="space-between">
                                <Stack>
                                    <Typography>Upload a video</Typography>
                                    <input
                                        className="upload-video" type="file" name="file"
                                        accept="video/mp4,video/avi,video/quicktime,video/x-ms-wmv,video/x-msvideo"
                                        onChange={validateVideoFormat} hidden
                                    />
                                    <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                        <Fab size="small" variant="extended"
                                            onClick={() => document.querySelector(".upload-video").click()}
                                            sx={{ width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none", "&:hover": { backgroundColor: "#003366" } }}
                                        >Choose File</Fab>
                                        <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {videoFileName ?? "No file chosen"}
                                        </span>
                                    </Stack>
                                    <p style={{ fontSize: "0.8rem", marginTop: 0 }}>
                                        {videoSizeLimitMB
                                            ? `Video size should not exceed ${videoSizeLimitMB} ${videoSizeLimitUnit || "MB"}.`
                                            : `Video size should not exceed ${DEFAULT_PLATFORM_VIDEO_LIMIT_MB} MB.`}
                                    </p>
                                </Stack>
                            </Grid>
                        )}

                        {/* IntelliDash Dashboard */}
                        {addContentType === "IntelliDash Dashboard" && (
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" size="small" value={contentWorkbook} label="Workbook Name" required disabled sx={{ width: "100%" }}
                                        error={!!addContentErrorMessages.workbookError} helperText={addContentErrorMessages.workbookError}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Button variant="outlined" size="small" color="primary" onClick={handleSelectWorkbook}
                                                        sx={{ textTransform: "none", left: "10px", fontSize: "12px", "&:hover": { bgcolor: "#003366", color: "#fff" } }}
                                                    >Select Workbook</Button>
                                                </InputAdornment>
                                            ),
                                        }}
                                        InputLabelProps={{ shrink: contentWorkbook ? true : false }}
                                    />
                                </Grid>
                                {contentWorkbook && (
                                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        <Autocomplete size="small" options={dashboardList} value={contentDashboard}
                                            onChange={(e, value) => handleDashboardChange(value)}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Select Dashboard" required
                                                    error={!!addContentErrorMessages.dashboardError} helperText={addContentErrorMessages.dashboardError}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                                {contentDashboard && (
                                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        <Autocomplete size="small" options={customViewList} value={contentView}
                                            onChange={(e, value) => {
                                                const newBreadcrumUrl = value
                                                    ? `${contentModule}->${contentWorkbook}->${contentDashboard}: View-${value}`
                                                    : `${contentModule}->${contentWorkbook}->${contentDashboard}`;
                                                setBreadcrumUrl(newBreadcrumUrl);
                                                setAddContentErrorMessages({ customViewError: value ? "" : addContentErrorMessages.customViewError });
                                                setContentView(value);
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Select View" required
                                                    error={!!addContentErrorMessages.customViewError} helperText={addContentErrorMessages.customViewError}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                                {contentWorkbook && (
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <a href="#" style={{ color: "#003366" }}>{breadcrumUrl}</a>
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Open URL */}
                        {addContentType === "Open URL" && (
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" label="URL" size="small" required value={addContentOpenUrl} autoComplete="off" sx={{ width: "100%" }}
                                        onChange={handleOpenUrlChange}
                                        error={!!addContentErrorMessages.openUrlError} helperText={addContentErrorMessages.openUrlError}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={refreshRateOptions} value={addContentRefreshRate}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                        onChange={(e, value) => setAddContentRefreshRate(value ?? refreshRateOptions[0])}
                                        renderInput={(params) => <TextField {...params} variant="outlined" label="Refresh Rate" />}
                                        ListboxProps={{ sx: { scrollbarWidth: "thin" } }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Machine Metrics */}
                        {addContentType === "Machine Metrics" && (
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={metricsDashboardTypeList}
                                        getOptionLabel={(option) => option.ic_mm_dashboard_name}
                                        onChange={(_, value) => handleDashboardTypeChange(value)}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="outlined" label="Dashboard Type" required
                                                error={!!addContentErrorMessages.dashboardTypeError} helperText={addContentErrorMessages.dashboardTypeError}
                                            />
                                        )}
                                    />
                                </Grid>
                                {dashboardType && (
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <TextField variant="outlined" label="Machine Metrics Dashboard URL" size="small" required
                                            value={metricsDashboardUrl} sx={{ width: "100%" }} onChange={handleDashboardURLChange}
                                            error={!!addContentErrorMessages.metricsDashboardUrlError}
                                            helperText={addContentErrorMessages.metricsDashboardUrlError} autoComplete="off"
                                            InputProps={{
                                                endAdornment: (
                                                    <>
                                                        <IconButton edge="end" aria-label="dashboardUrlInfo" onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose} onClick={handleTooltipOpen}>
                                                            <InfoIcon color="info" />
                                                        </IconButton>
                                                        <CustomTooltip anchorEl={anchorEl} open={open} handleClose={handleTooltipClose} content={tooltipContent} />
                                                    </>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Acumatica */}
                        {addContentType === "Acumatica" && (
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" label="Acumatica Dashboard URL" size="small" required
                                        value={metricsDashboardUrl} sx={{ width: "100%" }} onChange={handleDashboardURLChange}
                                        error={!!addContentErrorMessages.metricsDashboardUrlError}
                                        helperText={addContentErrorMessages.metricsDashboardUrlError} autoComplete="off"
                                        InputProps={{
                                            endAdornment: (
                                                <>
                                                    <IconButton edge="end" aria-label="dashboardUrlInfo" onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose} onClick={handleTooltipOpen}>
                                                        <InfoIcon color="info" />
                                                    </IconButton>
                                                    <CustomTooltip anchorEl={anchorEl} open={open} handleClose={handleTooltipClose} content={tooltipContentAcumatica} />
                                                </>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={refreshRateOptions} value={addContentRefreshRate}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                        onChange={(e, value) => setAddContentRefreshRate(value ?? refreshRateOptions[0])}
                                        renderInput={(params) => <TextField {...params} variant="outlined" label="Refresh Rate" />}
                                        ListboxProps={{ sx: { scrollbarWidth: "thin" } }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ marginRight: "20px" }}>
                    <Button type="submit" variant="contained" size="small" onClick={handleSaveContent}
                        sx={{ textTransform: "none", bgcolor: "#003366", "&:hover": { bgcolor: "#0d45a5" } }}
                    >Save</Button>
                    <Button variant="contained" size="small" color="error" onClick={closeAddContentModal} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </>
    );
};

export default CreateContentDialog;